import { z } from 'zod';
import { assetsAssetKindSchema } from './assets-asset.contract.js';
export const assetsRatesCapability = 'assets.rates.v1';
export const assetsRateSummariesCapability = 'assets.rate-summaries.v1';
const moneyCentsSchema = z.number().int().min(0).max(9_000_000_000);
export const assetsCurrencyCodeSchema = z.string().regex(/^[A-Z]{3}$/);
export const assetsTimeZoneSchema = z.string().trim().min(1).max(100).refine((value) => {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: value }).format(new Date(0));
        return true;
    }
    catch {
        return false;
    }
}, 'Invalid IANA time zone.');
export const assetsRateScopeSchema = z.discriminatedUnion('type', [
    z.strictObject({ type: z.literal('asset-kind'), assetKind: assetsAssetKindSchema }),
    z.strictObject({ type: z.literal('asset'), assetId: z.string().uuid() }),
]);
export const assetsRateInputSchema = z.strictObject({
    scope: assetsRateScopeSchema,
    effectiveAt: z.string().datetime({ offset: true }),
    currencyCode: assetsCurrencyCodeSchema,
    timeZone: assetsTimeZoneSchema,
    dailyCostCents: moneyCentsSchema,
    markupBasisPoints: z.number().int().min(0).max(1_000_000),
    dailyChargeCents: moneyCentsSchema,
    reason: z.string().trim().min(10).max(500),
});
export const assetsRateVersionSchema = assetsRateInputSchema.extend({
    contractVersion: z.literal(assetsRatesCapability),
    tenantId: z.string().uuid(),
    id: z.string().uuid(),
    revision: z.number().int().positive(),
    createdByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
});
export const assetsCreateRateVersionCommandSchema = z.strictObject({
    commandId: z.string().uuid(),
    rateId: z.string().uuid(),
    rate: assetsRateInputSchema,
});
export const assetsRateListQuerySchema = z.strictObject({
    assetKind: assetsAssetKindSchema.nullable().default(null),
    assetId: z.string().uuid().nullable().default(null),
    effectiveAtOrBefore: z.string().datetime({ offset: true }).nullable().default(null),
    limit: z.number().int().min(1).max(500).default(100),
    offset: z.number().int().nonnegative().default(0),
});
const pageSchema = z.strictObject({
    limit: z.number().int().positive(),
    offset: z.number().int().nonnegative(),
    returned: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
});
export const assetsRateVersionsResponseSchema = z.strictObject({
    data: z.array(assetsRateVersionSchema),
    page: pageSchema,
});
export const assetsRateVersionResponseSchema = z.strictObject({
    data: assetsRateVersionSchema,
    command: z.strictObject({ commandId: z.string().uuid(), idempotent: z.boolean() }).optional(),
});
export const assetsPlacementRateSnapshotSchema = z.strictObject({
    source: z.enum(['configured', 'configured-masked', 'unpriced']),
    rateVersionId: z.string().uuid().nullable(),
    rateRevision: z.number().int().positive().nullable(),
    currencyCode: assetsCurrencyCodeSchema,
    timeZone: assetsTimeZoneSchema,
    dailyCostCents: moneyCentsSchema.nullable(),
    markupBasisPoints: z.number().int().min(0).max(1_000_000).nullable(),
    dailyChargeCents: moneyCentsSchema.nullable(),
}).superRefine((snapshot, context) => {
    const hasIdentity = snapshot.rateVersionId !== null && snapshot.rateRevision !== null;
    const hasMoney = snapshot.dailyCostCents !== null && snapshot.markupBasisPoints !== null &&
        snapshot.dailyChargeCents !== null;
    const valid = snapshot.source === 'configured'
        ? hasIdentity && hasMoney
        : snapshot.source === 'configured-masked'
            ? hasIdentity && !hasMoney
            : !hasIdentity && !hasMoney;
    if (!valid) {
        context.addIssue({
            code: 'custom',
            path: ['source'],
            message: 'Placement rate source must match its immutable identity and money visibility.',
        });
    }
});
//# sourceMappingURL=assets-rate.contract.js.map